import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Upload, CheckCircle, AlertTriangle, HelpCircle, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { useEtablissement } from '@/contexts/EtablissementContext';
import { useMembresQuery } from '@/hooks/queries/useMembresQuery';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

interface ScanAttestationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ScanResult {
  fileName: string;
  status: 'matched' | 'ambiguous' | 'not_found' | 'uploaded' | 'error';
  matchedName?: string;
  matchedMembreId?: string;
  message?: string;
}

/**
 * Modal de scan d'attestations en masse
 * Upload multiple de PDFs → upload Storage → traitement IA → rattachement aux membres
 */
export const ScanAttestationsDialog = ({ open, onOpenChange }: ScanAttestationsDialogProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [step, setStep] = useState<'upload' | 'processing' | 'results' | 'done'>('upload');
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ScanResult[]>([]);
  const { etablissement } = useEtablissement();
  const { data: membres } = useMembresQuery();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selected]);
  };

  /**
   * Traitement réel : upload chaque fichier vers Storage + appel process-attestation
   */
  const handleProcess = async () => {
    if (!etablissement?.id) {
      toast({ title: 'Erreur', description: 'Établissement non trouvé', variant: 'destructive' });
      return;
    }

    setStep('processing');
    const scanResults: ScanResult[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress(Math.round(((i + 1) / files.length) * 100));

      try {
        // Essayer de matcher le fichier à un membre existant par le nom du fichier
        const matchedMembre = tryMatchFileName(file.name);

        if (!matchedMembre) {
          // Upload quand même mais marquer comme "non rattaché"
          const storagePath = `${etablissement.id}/non_rattache/${Date.now()}_${file.name}`;
          await supabase.storage.from('attestations').upload(storagePath, file, {
            contentType: file.type,
          });

          scanResults.push({
            fileName: file.name,
            status: 'not_found',
            message: 'Aucun membre correspondant trouvé',
          });
          continue;
        }

        // Upload le fichier dans le dossier du membre
        const fileExt = file.name.split('.').pop();
        const storagePath = `${etablissement.id}/${matchedMembre.id}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('attestations')
          .upload(storagePath, file, { contentType: file.type });

        if (uploadError) {
          scanResults.push({
            fileName: file.name,
            status: 'error',
            message: uploadError.message,
          });
          continue;
        }

        // Créer ou mettre à jour l'attestation
        const { data: attestation } = await supabase
          .from('attestations')
          .insert({
            membre_id: matchedMembre.id,
            etablissement_id: etablissement.id,
            statut: 'en_attente_verification',
            storage_path: storagePath,
            nom_fichier: file.name,
            // received_at = date de réception réelle du document (scan en masse).
            // Row fraîchement créée, aucun risque d'écraser une valeur existante.
            received_at: new Date().toISOString(),
          })
          .select()
          .single();

        // Déclencher le traitement IA
        if (attestation) {
          supabase.functions.invoke('process-attestation', {
            body: {
              attestation_id: attestation.id,
              storage_path: storagePath,
              membre_id: matchedMembre.id,
              etablissement_id: etablissement.id,
            },
          }).catch(err => console.error('Erreur process-attestation:', err));
        }

        scanResults.push({
          fileName: file.name,
          status: 'matched',
          matchedName: `${matchedMembre.prenom} ${matchedMembre.nom}`,
          matchedMembreId: matchedMembre.id,
        });
      } catch (err: any) {
        scanResults.push({
          fileName: file.name,
          status: 'error',
          message: err.message || 'Erreur inattendue',
        });
      }
    }

    setResults(scanResults);
    setStep('results');
  };

  /**
   * Tente de matcher un nom de fichier avec un membre existant
   * Recherche fuzzy basique : cherche prénom+nom ou nom dans le filename
   */
  const tryMatchFileName = (fileName: string) => {
    if (!membres) return null;
    const normalizedName = fileName.toLowerCase().replace(/[^a-zàâéèêëïôùûüç\s-]/g, ' ').trim();

    for (const membre of membres) {
      const fullName = `${membre.prenom} ${membre.nom}`.toLowerCase();
      const reverseName = `${membre.nom} ${membre.prenom}`.toLowerCase();
      const nomOnly = membre.nom.toLowerCase();

      if (
        normalizedName.includes(fullName) ||
        normalizedName.includes(reverseName) ||
        normalizedName.includes(nomOnly)
      ) {
        return membre;
      }
    }
    return null;
  };

  const handleFinalize = () => {
    queryClient.invalidateQueries({ queryKey: ['membres'] });
    setStep('done');
  };

  const handleClose = () => {
    setFiles([]);
    setResults([]);
    setProgress(0);
    setStep('upload');
    onOpenChange(false);
  };

  const matchedCount = results.filter(r => r.status === 'matched').length;
  const notFoundCount = results.filter(r => r.status === 'not_found').length;
  const errorCount = results.filter(r => r.status === 'error').length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="app-content-centered-dialog max-w-[760px] rounded-[24px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface))] p-0 shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <DialogHeader className="border-b border-[hsl(var(--app-border-subtle))] px-6 pb-5 pt-6">
          <DialogTitle className="text-[20px] font-semibold tracking-[-0.02em] text-[hsl(var(--app-text-strong))]">
            Scan d'attestations en masse
          </DialogTitle>
          <DialogDescription className="mt-1 text-[14px] leading-6 text-[hsl(var(--app-text-muted))]">
            Uploadez les attestations que vous avez déjà. Elles seront rattachées aux membres existants et vérifiées automatiquement.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5">
          {step === 'upload' && (
            <div className="space-y-4">
              {/* Zone upload multiple */}
              <label className="flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded-[18px] border border-dashed border-[hsl(var(--app-border-strong))] bg-[hsl(var(--app-surface-muted))] transition-colors hover:border-primary hover:bg-[hsl(var(--app-accent-soft))]">
                <Upload className="mb-2 h-8 w-8 text-[hsl(var(--app-text-muted))]" />
                <p className="text-[14px] text-[hsl(var(--app-text))]">
                  Glissez vos attestations ici ou <span className="font-medium text-[hsl(var(--app-accent-strong))]">parcourir</span>
                </p>
                <p className="mt-1 text-[12px] text-[hsl(var(--app-text-muted))]">
                  PDF · JPG · PNG — Upload multiple autorisé
                </p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  className="hidden"
                  onChange={handleFilesChange}
                />
              </label>

              {/* Conseil de nommage */}
              <div className="rounded-[16px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] p-3.5">
                <p className="text-[12px] leading-5 text-[hsl(var(--app-text))]">
                  💡 <strong>Astuce :</strong> Nommez vos fichiers avec le nom du membre (ex: "dupont_marie.pdf") pour un rattachement automatique.
                </p>
              </div>

              {/* Liste des fichiers sélectionnés */}
              {files.length > 0 && (
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between rounded-[14px] border border-[hsl(var(--app-border-subtle))] bg-white px-3 py-2.5">
                      <span className="truncate text-[13px] text-[hsl(var(--app-text))]">{f.name}</span>
                      <button
                        onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                        className="shrink-0 text-[12px] font-medium text-rose-600 hover:text-rose-700"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <p className="pt-1 text-[12px] text-[hsl(var(--app-text-muted))]">
                    {files.length} fichier{files.length > 1 ? 's' : ''} sélectionné{files.length > 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 'processing' && (
            <div className="text-center py-8 space-y-4">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-[hsl(var(--app-text))]">
                Upload et analyse en cours... {Math.round(progress * files.length / 100)}/{files.length}
              </p>
              <Progress value={progress} className="w-full" />
              <p className="text-[12px] text-[hsl(var(--app-text-muted))]">
                Chaque fichier est uploadé, rattaché et envoyé pour vérification automatique.
              </p>
            </div>
          )}

          {step === 'results' && (
            <div className="space-y-4">
              {/* Résumé */}
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  {matchedCount} rattaché{matchedCount > 1 ? 's' : ''}
                </span>
                {notFoundCount > 0 && (
                  <span className="flex items-center gap-1.5">
                    <HelpCircle className="h-4 w-4 text-[hsl(var(--app-text-muted))]" />
                    {notFoundCount} non rattaché{notFoundCount > 1 ? 's' : ''}
                  </span>
                )}
                {errorCount > 0 && (
                  <span className="flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4 text-rose-500" />
                    {errorCount} erreur{errorCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {/* Tableau de résultats */}
              <div className="max-h-56 overflow-y-auto rounded-[18px] border border-[hsl(var(--app-border-subtle))] bg-white">
                <table className="w-full text-sm">
                  <tbody>
                    {results.map((r, i) => (
                      <tr key={i} className="border-t border-[hsl(var(--app-border-subtle))] first:border-t-0">
                        <td className="px-3 py-2.5">
                          {r.status === 'matched' && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                          {r.status === 'not_found' && <HelpCircle className="h-4 w-4 text-[hsl(var(--app-text-muted))]" />}
                          {r.status === 'error' && <AlertTriangle className="h-4 w-4 text-rose-500" />}
                        </td>
                        <td className="max-w-[200px] truncate px-3 py-2.5 text-[13px] text-[hsl(var(--app-text))]">{r.fileName}</td>
                        <td className="px-3 py-2.5 text-[12px] text-[hsl(var(--app-text-muted))]">
                          {r.status === 'matched' && `→ ${r.matchedName}`}
                          {r.status === 'not_found' && r.message}
                          {r.status === 'error' && <span className="text-rose-600">{r.message}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {notFoundCount > 0 && (
                <p className="text-[12px] text-[hsl(var(--app-text-muted))]">
                  Les fichiers non rattachés ont été uploadés. Vous pourrez les rattacher manuellement depuis la fiche du membre.
                </p>
              )}
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-8">
              <CheckCircle className="mx-auto mb-3 h-12 w-12 text-emerald-500" />
              <p className="text-lg font-medium text-[hsl(var(--app-text-strong))]">Import finalisé</p>
              <p className="mt-1 text-sm text-[hsl(var(--app-text-muted))]">
                {matchedCount} attestation{matchedCount > 1 ? 's' : ''} rattachée{matchedCount > 1 ? 's' : ''} et envoyée{matchedCount > 1 ? 's' : ''} pour vérification.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-[hsl(var(--app-border-subtle))] px-6 py-4">
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={handleClose} className="rounded-[14px] border-[hsl(var(--app-border-subtle))]">Annuler</Button>
              <Button
                onClick={handleProcess}
                disabled={files.length === 0}
                className="rounded-[14px]"
              >
                Analyser {files.length} fichier{files.length > 1 ? 's' : ''}
              </Button>
            </>
          )}
          {step === 'results' && (
            <>
              <Button variant="outline" onClick={handleClose} className="rounded-[14px] border-[hsl(var(--app-border-subtle))]">Fermer</Button>
              <Button onClick={handleFinalize} className="rounded-[14px]">
                Terminer
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={handleClose} className="rounded-[14px]">
              Fermer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
